import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";
import AdminDeleteButton from "@/components/AdminDeleteButton";

/** Generic simple list page with a create form and inline edit for basic entities. */
export function EntityPage({ title, endpoint, columns, defaults, subtitle, testid = "entity", extraFormFooter }) {
  const [items, setItems] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(defaults);

  const load = async () => {
    const { data } = await api.get(endpoint);
    setItems(data);
  };
  useEffect(() => { load(); }, [endpoint]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post(endpoint, form);
      toast.success(`${title.slice(0,-1)} added`);
      setForm(defaults);
      setShowAdd(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="label-tiny mb-1">{subtitle || "Directory"}</div>
          <h1 className="font-display text-3xl tracking-tight">{title}</h1>
        </div>
        <button className="btn-primary flex items-center gap-2" onClick={() => setShowAdd(v=>!v)} data-testid={`${testid}-add-btn`}>
          {showAdd ? <><X size={16}/> Cancel</> : <><Plus size={16}/> New</>}
        </button>
      </div>

      {showAdd && (
        <form onSubmit={submit} className="card-surface p-5 grid grid-cols-1 md:grid-cols-2 gap-4 fade-up" data-testid={`${testid}-form`}>
          {columns.filter(c => c.editable !== false).map(c => (
            <div key={c.key}>
              <div className="label-tiny mb-1.5">{c.label}</div>
              {c.type === "select" ? (
                <select className="input-field" value={form[c.key] ?? ""} onChange={e=>setForm({...form,[c.key]: e.target.value})}>
                  {c.options.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : c.type === "number" ? (
                <input type="number" step="any" className="input-field" value={form[c.key] ?? ""} onChange={e=>setForm({...form,[c.key]: Number(e.target.value)})} data-testid={`${testid}-field-${c.key}`} />
              ) : c.type === "textarea" ? (
                <textarea className="input-field" rows={2} value={form[c.key] ?? ""} onChange={e=>setForm({...form,[c.key]: e.target.value})} />
              ) : c.type === "date" ? (
                <input type="date" className="input-field" value={form[c.key] ?? ""} onChange={e=>setForm({...form,[c.key]: e.target.value})} data-testid={`${testid}-field-${c.key}`} />
              ) : (
                <input className="input-field" value={form[c.key] ?? ""} onChange={e=>setForm({...form,[c.key]: e.target.value})} data-testid={`${testid}-field-${c.key}`} />
              )}
            </div>
          ))}
          <div className="md:col-span-2 flex justify-end">
            <button type="submit" className="btn-primary" data-testid={`${testid}-submit`}>Save</button>
          </div>
          {extraFormFooter}
        </form>
      )}

      <div className="card-surface overflow-x-auto">
        <table className="data-table">
          <thead>
            <tr>{columns.map(c => <th key={c.key}>{c.label}</th>)}<th></th></tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={columns.length + 1} className="p-6 text-center text-[#757169]">No records yet.</td></tr>
            )}
            {items.map(it => (
              <tr key={it.id} data-testid={`${testid}-row-${it.id}`}>
                {columns.map(c => <td key={c.key}>{c.render ? c.render(it) : (it[c.key] ?? "—")}</td>)}
                <td>
                  <AdminDeleteButton
                    endpoint={`${endpoint}/${it.id}`}
                    label={`${title.slice(0,-1).toLowerCase()} "${it.name || it.subject || it.description || it.id.slice(0,6)}"`}
                    onDone={load}
                    testid={`${testid}-delete-${it.id}`}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
